import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Códigos de obrigação fiscal (consts TaxObligationKinds no backend). */
export type TaxObligationKind =
  | "iva"
  | "irc"
  | "irs"
  | "seguranca_social"
  | "outro";

export interface TaxObligation {
  id: number;
  kind: TaxObligationKind;
  description?: string | null;
  periodLabel?: string | null;
  amount: number;
  dueDate: string;
  isPaid: boolean;
  paidAt?: string | null;
  created: string;
  lastModified?: string | null;
}

export interface TaxObligationPayload {
  kind: TaxObligationKind;
  description?: string | null;
  periodLabel?: string | null;
  amount: number;
  dueDate: string;
}

/** Sugestão de provisão de IVA calculada a partir das faturas do período. */
export interface IvaProvisionSuggestion {
  periodLabel: string;
  vatCollected: number;
  vatPaid: number;
  suggestedAmount: number;
  suggestedDueDate: string;
}

/** Granularidade aceite pelo endpoint de sugestão de IVA (§2.3/§6). */
export type IvaSuggestionGranularity = "monthly" | "quarterly";

export interface IvaProvisionSuggestionParams {
  year: number;
  granularity: IvaSuggestionGranularity;
  period: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  // 204 No Content (DELETE) → nada para desserializar.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function getTaxObligations(): Promise<TaxObligation[]> {
  return request<TaxObligation[]>("/api/tax-obligations");
}

export function createTaxObligation(
  payload: TaxObligationPayload,
): Promise<TaxObligation> {
  return request<TaxObligation>("/api/tax-obligations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateTaxObligation(
  id: number,
  payload: TaxObligationPayload,
): Promise<TaxObligation> {
  return request<TaxObligation>(`/api/tax-obligations/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteTaxObligation(id: number): Promise<void> {
  return request<void>(`/api/tax-obligations/${id}`, {
    method: "DELETE",
  });
}

export function markTaxObligationPaid(
  id: number,
  paidAt?: string | null,
): Promise<TaxObligation> {
  return request<TaxObligation>(`/api/tax-obligations/${id}/mark-paid`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paidAt: paidAt ?? null }),
  });
}

export function getIvaProvisionSuggestion(
  params: IvaProvisionSuggestionParams,
): Promise<IvaProvisionSuggestion> {
  const search = new URLSearchParams();
  search.set("year", String(params.year));
  search.set("granularity", params.granularity);
  search.set("period", String(params.period));
  return request<IvaProvisionSuggestion>(
    `/api/tax-obligations/iva-suggestion?${search.toString()}`,
  );
}
